import React from 'react';
import { useTheme } from '@/components/layout/theme-provider';
import { cn } from '@/lib/utils';
// Import white variant images for light mode
import white1 from '@/assets/tech_partners/AWS_Logo_white.svg';
import white2 from '@/assets/tech_partners/Axis_logo_white.svg';
import white3 from '@/assets/tech_partners/Hikvision_logo_white.svg';
import white4 from '@/assets/tech_partners/Intel_logo_white.svg';
import white5 from '@/assets/tech_partners/Nvidia_logo_white.svg';
import white6 from '@/assets/tech_partners/Dahua_logo_white.svg';

// Import black variant images for dark mode
import black1 from '@/assets/tech_partners/AWS_Logo_black.svg';
import black2 from '@/assets/tech_partners/Axis_logo_black.svg';
import black3 from '@/assets/tech_partners/Hikvision_logo_black.svg';
import black4 from '@/assets/tech_partners/Intel_logo_black.svg';
import black5 from '@/assets/tech_partners/Nvidia_logo_black.svg';
import black6 from '@/assets/tech_partners/Dahua_logo_black.svg';

// Arrays of partner images for both themes
const whiteImages = [white1, white2, white3, white4, white5, white6];
const blackImages = [black1, black2, black3, black4, black5, black6];

// Props: allow turning marquee scroll on/off
type TechPartnerProps = {
  scroll?: boolean;
  forceWhite?: boolean; // when true, always use white logos regardless of theme
  compact?: boolean; // when true and static, render smaller logos/gaps
};

export const TechPartner: React.FC<TechPartnerProps> = ({ scroll = true, forceWhite = false, compact = false }) => {
  const { theme } = useTheme();
  const [isDark, setIsDark] = React.useState(false);

  React.useEffect(() => {
    // Handle system theme preference
    const checkDark = () => {
      if (theme === 'system') {
        return window.matchMedia('(prefers-color-scheme: dark)').matches;
      }
      return theme === 'dark';
    };

    setIsDark(checkDark());

    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => setIsDark(checkDark());
    mediaQuery.addEventListener('change', handleChange);

    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);
  
  // Choose logo set: always white when forced, else theme-based
  const partnerImages = forceWhite ? whiteImages : (isDark ? whiteImages : blackImages);

  // Animation speed: faster in compact (hero), original elsewhere
  const marqueeDuration = compact ? '30s' : '45s';

  return (
    <div
      className={cn(
        'relative w-full',
        scroll ? 'overflow-hidden' : 'overflow-x-auto no-scrollbar',
        !scroll && compact ? 'tp-static-compact' : ''
      )}
      style={{
        scrollbarWidth: !scroll ? 'none' : undefined,
        msOverflowStyle: !scroll ? ('none' as any) : undefined,
        // Smooth transparent edge fades only for compact (hero)
        WebkitMaskImage:
          scroll && compact
            ? 'linear-gradient(to right, transparent 0%, rgba(0,0,0,1) 8%, rgba(0,0,0,1) 92%, transparent 100%)'
            : undefined,
        maskImage:
          scroll && compact
            ? 'linear-gradient(to right, transparent 0%, rgba(0,0,0,1) 8%, rgba(0,0,0,1) 92%, transparent 100%)'
            : undefined,
        WebkitMaskSize: scroll && compact ? '100% 100%' : undefined,
        maskSize: scroll && compact ? '100% 100%' : undefined,
      }}
    >
      {/* Re-introduce soft white/dark fades for non-compact usages (original behaviour) */}
      {scroll && !compact && (
        <>
          <div
            className="absolute left-0 top-0 h-full w-32 z-10 pointer-events-none"
            style={{
              background:
                'linear-gradient(90deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 100%)',
            }}
          />
          <div
            className="absolute right-0 top-0 h-full w-32 z-10 pointer-events-none"
            style={{
              background:
                'linear-gradient(270deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 100%)',
            }}
          />
        </>
      )}
      <div
        className="flex items-center py-4 sm:py-6"
        style={{
          animation: scroll ? `horizontal-marquee ${marqueeDuration} linear infinite` : 'none',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: scroll ? 'center' : 'flex-start',
          flexWrap: 'nowrap',
          gap: scroll ? (compact ? '1rem' : '5rem') : (compact ? '0.5rem' : '0.75rem'),
          width: 'max-content',
          padding: compact ? '0 0.25rem' : '0 2rem',
        }}
      >
        {(scroll ? [...partnerImages, ...partnerImages] : partnerImages).map(
          (img: string, idx: number) => (
            <div
              key={idx}
              className="logo-item relative flex items-center justify-center"
              style={{
                flex: '0 0 auto',
                padding: compact ? '0 0.25rem' : '0 4rem',
                width: scroll
                  ? compact
                    ? '96px'
                    : '200px'
                  : compact
                    ? '90px'
                    : '100px',
                height: scroll
                  ? compact
                    ? '44px'
                    : '100px'
                  : compact
                    ? '48px'
                    : '52px',
              }}
            >
              <img
                src={img}
                alt={`Partner ${(idx % partnerImages.length) + 1}`}
                className="w-auto object-contain"
                loading="lazy"
                style={{
                  maxWidth: scroll
                    ? compact
                      ? '60px'
                      : '160px'
                    : compact
                      ? '60px'
                      : '70px',
                  height: 'auto',
                  width: '100%',
                }}
              />
            </div>
          )
        )}
      </div>

      <style>{`
        /* Hide scrollbar for WebKit */
        .no-scrollbar::-webkit-scrollbar { display: none; }
        @keyframes horizontal-marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }

        /* Responsive sizing for compact static mode */
        @media (min-width: 640px) { /* sm */
          .tp-static-compact .logo-item { width: 110px !important; height: 56px !important; }
          .tp-static-compact .logo-item img { max-width: 110px !important; }
        }
        @media (min-width: 1024px) { /* lg */
          .tp-static-compact .logo-item { width: 120px !important; height: 60px !important; }
          .tp-static-compact .logo-item img { max-width: 120px !important; }
        }

        /* Dark-mode overrides for non-compact gradient fades (original behaviour) */
        @media (prefers-color-scheme: dark) {
          .dark .absolute.left-0 {
            background: linear-gradient(90deg, rgba(30,41,59,1) 0%, rgba(30,41,59,0) 100%) !important;
          }
          .dark .absolute.right-0 {
            background: linear-gradient(270deg, rgba(30,41,59,1) 0%, rgba(30,41,59,0) 100%) !important;
          }
        }
      `}</style>
    </div>
  );
};